import React from "react";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SxProps } from "@mui/material";
import { formatNumber } from "../../utils/numberUtils";
import { EstimateCreditsDetailResponse } from "../../apis/exportTrackingApi";
import { CreditsBreakdownTable } from "./CreditsBreakdownTable";

interface ExportCreditsAlertProps {
  estimate: EstimateCreditsDetailResponse | null;
  isLoading: boolean;
  error?: boolean;
  sx?: SxProps;
}

export const ExportCreditsAlert: React.FC<ExportCreditsAlertProps> = ({
  estimate,
  isLoading,
  error = false,
  sx = {},
}) => {
  if (!isLoading && estimate === null && !error) {
    return null;
  }

  const severity = error ? "error" : "info";
  const borderColor = error ? "error.main" : "info.main";

  return (
    <Alert
      severity={severity}
      sx={{
        bgcolor: "white",
        fontSize: 12,
        border: "1px solid",
        borderColor,
        alignItems: isLoading ? "center" : "flex-start",
        "& .MuiAlert-message": {
          width: "100%",
          minWidth: 0,
          overflow: "visible",
        },
        ...sx,
      }}
    >
      {isLoading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body1" color="inherit">
            Estimating credits usage... please wait.
          </Typography>
        </Box>
      ) : error ? (
        <>
          <Typography variant="body1" color="inherit">
            Unable to estimate credits for this report
          </Typography>
          <Typography variant="body2" color="inherit">
            Please contact{" "}
            <a href="mailto:customersupport@hginsights.com">
              customersupport@hginsights.com
            </a>{" "}
            for assistance.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body1" color="inherit">
            Up to{" "}
            <strong data-testid="export-credits-total">
              {estimate ? formatNumber(estimate.credits_estimate) : ""}
            </strong>{" "}
            credits will be used for this export.
          </Typography>
          <Typography variant="body2" color="inherit">
            Select EXPORT below to proceed.
          </Typography>

          {estimate && (
            <CreditsBreakdownTable details={estimate.details} sx={{ mt: 1 }} />
          )}
        </>
      )}
    </Alert>
  );
};
